// Artemis chat state (WEB-7092). Messages are keyed by a client id so a stream always
// updates the message it belongs to, however many sends overlap.
//
// Message: { id, question, answer, status, sources, queryId, suggestedQuestions, stats,
//            useful, errorCode, tools, actions }, and each source may carry disliked.
// sources: the documents behind the reply, from metadata or, in agent mode, from the searches it ran.
// tools: server tool calls in agent mode, [{ toolUseId, tool, status, message }], one per
//   ToolUseId, updated in place (parallel calls overlap, so never by arrival order).
// steps: protocol 2 units of work, [{ stepId, kind, status, title, summary, tool, message, thinking, result }],
//   one per StepId, updated in place; thinking events append to their step's thinking text. result is
//   a succeeded step's Result (API-3638): a saved marker or shape, regulations or application periods.
// removed: ids of objects Artemis saved that the user then removed from the chat, as "marker:12".
// actions: client actions the app ran: set_layer_visibility [{ toolUseId, tool, outcome, layerId, layerName, visible }]
//   and set_map_view [{ toolUseId, tool, outcome, label, view, previous, undone }];
//   visible follows the layer when the user undoes or redoes the change from the chat, and asked
//   then keeps what Artemis asked for.
// citations: [{ index, title, url, citeUrl }] from citation events, for the answer's [n] markers.
// statusText: the latest status event's line, cleared once the answer starts.
// startedAt, answerStartedAt, endedAt: times (ms) the reply was asked, started answering and ended,
//   for the elapsed and "thought for" labels. Actions that end or advance a reply carry at.
// messageId, conversationId: the saved reply's ids on v3 (the "message" event).
// status is 'streaming', 'complete', 'failed' (errorCode says why), 'stopped', or 'saving' for a
// reopened v3 reply the server was still writing.

export const initialChatState = []

let nextId = 0
export const newMessageId = () => `m${Date.now().toString(36)}-${(nextId++).toString(36)}`

// key: the document's store and id ("kb:12"); ids from the two stores can collide, so sources dedupe by it.
export const mapDocuments = (documents = []) => documents.map(doc => {
    let details = {}
    try { details = JSON.parse(doc.Details) || {} } catch (e) { /* Details isn't JSON; use the fallbacks */ }
    const source = {
        title: details.title || doc.Source || 'Unknown source',
        url: doc.Url,
        documentId: doc.Id
    }
    if (doc.Key) source.key = doc.Key
    return source
})

const sourceKey = source => source.key || `id:${source.documentId}`

const costOf = v => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null)

const applyStreamEvent = (message, event, at = null) => {
    switch (event.Type) {
        case 'metadata':
            return {
                ...message,
                queryId: event.QueryId ?? message.queryId,
                sources: mapDocuments(event.Documents || []),
                // The view the question was asked from, named (API-3644), when the server could.
                view: event.View || message.view || null
            }
        case 'delta':
            return {
                ...message,
                answer: message.answer + (event.Text || ''),
                answerStartedAt: message.answerStartedAt || at,
                statusText: null
            }
        case 'done':
            // v3 ends a cancelled reply with done too.
            return {
                ...message,
                status: event.Status === 'cancelled' ? 'stopped' : 'complete',
                stats: event.Stats || null,
                // Staging only (API-3693): the reply's estimated model cost, in US dollars.
                cost: costOf(event.EstimatedCostUsd) ?? costOf(event.Stats && event.Stats.EstimatedCostUsd),
                endedAt: at
            }
        case 'message':
            return { ...message, messageId: event.MessageId ?? message.messageId, conversationId: event.ConversationId ?? message.conversationId, questionMessageId: event.ReplyToId ?? message.questionMessageId }
        case 'speech':
            // What to read aloud (API-3690): one per reply, so a repeat is ignored.
            return event.Text && !message.speech ? { ...message, speech: event.Text } : message
        case 'status':
            return event.Text && !message.answer ? { ...message, statusText: event.Text } : message
        case 'citation': {
            const index = Number(event.Index)
            if (!Number.isInteger(index)) return message
            const citation = {
                index,
                title: event.Title || '',
                url: event.Url || null,
                citeUrl: event.CiteUrl || null,
                locator: event.Locator || null,
                sourceType: event.SourceType || null,
                asOf: event.AsOf || null
            }
            const citations = (message.citations || []).filter(c => c.index !== index)
            return { ...message, citations: [...citations, citation].sort((a, b) => a.index - b.index) }
        }
        case 'tool': {
            if (!event.ToolUseId) return message
            const tool = { toolUseId: event.ToolUseId, tool: event.Tool, status: event.Status, message: event.Message || null }
            // Kept on the tool so its step row can name what the search found.
            if (Array.isArray(event.Documents) && event.Documents.length > 0) tool.documents = mapDocuments(event.Documents)
            const tools = message.tools || []
            const known = tools.some(t => t.toolUseId === tool.toolUseId)
            const updated = { ...message, tools: known ? tools.map(t => t.toolUseId === tool.toolUseId ? { ...t, ...tool } : t) : [...tools, tool] }
            // A search's documents are the reply's sources (WEB-7098); agent mode sends none up front.
            if (Array.isArray(event.Documents) && event.Documents.length > 0) {
                const seen = new Set(message.sources.map(sourceKey))
                const added = mapDocuments(event.Documents).filter(source => {
                    if (seen.has(sourceKey(source))) return false
                    seen.add(sourceKey(source))
                    return true
                })
                updated.sources = [...message.sources, ...added]
            }
            return updated
        }
        case 'step': {
            if (!event.StepId) return message
            const steps = message.steps || []
            const fields = {
                stepId: event.StepId,
                kind: event.Kind || 'tool',
                status: event.Status || 'started',
                title: event.Title || '',
                summary: event.Summary || null,
                tool: event.Tool || null,
                message: event.Message || null,
                // A failed step never has one.
                result: event.Status === 'succeeded' && event.Result && typeof event.Result === 'object' ? event.Result : null
            }
            const known = steps.some(s => s.stepId === event.StepId)
            return {
                ...message,
                steps: known
                    // A later event may leave out what an earlier one said; keep it.
                    ? steps.map(s => s.stepId === event.StepId ? {
                        ...s,
                        ...fields,
                        kind: event.Kind || s.kind,
                        title: fields.title || s.title,
                        summary: fields.summary || s.summary,
                        tool: fields.tool || s.tool,
                        result: fields.result || s.result || null
                    } : s)
                    : [...steps, { ...fields, thinking: '' }]
            }
        }
        case 'thinking': {
            if (!event.StepId || !event.Text) return message
            const steps = message.steps || []
            return {
                ...message,
                steps: steps.some(s => s.stepId === event.StepId)
                    ? steps.map(s => s.stepId === event.StepId ? { ...s, thinking: s.thinking + event.Text } : s)
                    : [...steps, { stepId: event.StepId, kind: 'thinking', status: 'started', title: 'Reasoning', summary: null, tool: null, message: null, thinking: event.Text }]
            }
        }
        default:
            // Unknown types are ignored, as the protocol asks. Errors arrive through 'failed'.
            return message
    }
}

const updateMessage = (state, id, update) => {
    let changed = false
    const next = state.map(message => {
        if (message.id !== id) return message
        const updated = update(message)
        if (updated !== message) changed = true
        return updated
    })
    return changed ? next : state
}

export const chatReducer = (state = initialChatState, action) => {
    switch (action.type) {
        case 'asked':
            return [...state, {
                id: action.id,
                question: action.question,
                answer: '',
                status: 'streaming',
                sources: [],
                queryId: null,
                suggestedQuestions: [],
                stats: null,
                tools: [],
                steps: [],
                actions: [],
                citations: [],
                statusText: null,
                startedAt: action.at || null,
                // Asked with read aloud on, so its speech is spoken (WEB-7116).
                readAloud: !!action.readAloud,
                answerStartedAt: null,
                endedAt: null
            }]
        case 'event':
            return updateMessage(state, action.id, message => applyStreamEvent(message, action.event, action.at || null))
        case 'failed':
            return updateMessage(state, action.id, message =>
                message.status === 'streaming' ? { ...message, status: 'failed', errorCode: action.code || 'internal_error', endedAt: action.at || null } : message)
        case 'removed':
            // A question the server refused before it started (usage_limit_reached): it was never asked.
            return state.filter(message => message.id !== action.id)
        case 'stopped':
            return updateMessage(state, action.id, message =>
                message.status === 'streaming' ? { ...message, status: 'stopped', endedAt: action.at || null } : message)
        case 'actionResult':
            return updateMessage(state, action.id, message => ({ ...message, actions: [...(message.actions || []), action.result] }))
        case 'viewToggled':
            // Undo / Redo on a set_map_view move: undone is true once the map is back where it was.
            return updateMessage(state, action.id, message => ({
                ...message,
                actions: (message.actions || []).map(a => a.toolUseId === action.toolUseId ? { ...a, undone: action.undone } : a)
            }))
        case 'actionToggled':
            return updateMessage(state, action.id, message => ({
                ...message,
                // asked keeps what Artemis asked for, so the row can say the change was undone.
                actions: (message.actions || []).map(a => a.toolUseId === action.toolUseId
                    ? { ...a, asked: a.asked === undefined ? a.visible : a.asked, visible: action.visible }
                    : a)
            }))
        case 'objectRemoved':
            return updateMessage(state, action.id, message => ({
                ...message,
                removed: [...(message.removed || []), action.objectKey]
            }))
        case 'prepend':
            // Earlier messages of a reopened chat, merged ahead of it by the caller.
            return action.messages
        case 'replaced':
            // A resent question the server answered with its saved reply instead of a stream.
            return updateMessage(state, action.id, message => ({ ...action.message, id: message.id }))
        case 'posted':
            // A group message that didn't mention Artemis: saved, with no reply to wait for.
            return updateMessage(state, action.id, message => ({ ...message, status: 'posted', questionMessageId: action.messageId, seq: action.seq, endedAt: action.at || null }))
        case 'reset':
            // Swaps in another chat: a new one, or one reopened from past chats.
            return action.messages || initialChatState
        case 'suggestions':
            return updateMessage(state, action.id, message => ({ ...message, suggestedQuestions: action.suggestedQuestions || [] }))
        case 'feedback':
            return updateMessage(state, action.id, message => ({ ...message, useful: action.useful }))
        case 'sourceFeedback':
            return updateMessage(state, action.id, message => ({
                ...message,
                sources: message.sources.map(src => src.documentId === action.documentId ? { ...src, disliked: action.disliked } : src)
            }))
        default:
            return state
    }
}
